import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Loader2, AlertCircle, Plus, CheckCircle } from 'lucide-react';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { AutomationSettingsLinkAlert } from '@/components/settings/AutomationSettingsLinkAlert';
import { AUTOMATION_TEMPLATE_UI } from '@/components/settings/automationTemplatesUi';
import { useAuth } from '@/hooks/use-auth';
import {
    CANONICAL_MEETING_VARIABLE_SPECS,
    MEETING_AUTOMATION_TEMPLATE_PRESETS,
    type MeetingAutomationTemplatePresetId,
} from '@/pages/meetingAutomationTemplateUtils';
import { useWhatsAppStatusQuery } from '@/queries';
import { whatsappService, type WABA } from '@/services/whatsappService';
import { resolvePreferredWhatsAppWabaId } from '@/services/whatsappWabaUtils';

interface Template {
    name: string;
    status: string;
    language: string;
    category: string;
    components: unknown[];
}

export function WhatsAppTemplatesList() {
    const { t } = useTranslation();
    const { lang } = useParams<{ lang: string }>();
    const { token } = useAuth();
    const hasAuthToken = Boolean(token && token.trim());
    const {
        data: whatsappStatus,
        error: whatsappStatusError,
        isLoading: loadingWhatsAppStatus,
    } = useWhatsAppStatusQuery({ enabled: hasAuthToken });
    const [wabas, setWabas] = useState<WABA[]>([]);
    const [selectedWaba, setSelectedWaba] = useState<string>('');
    const [templates, setTemplates] = useState<Template[]>([]);
    const [loadingWabas, setLoadingWabas] = useState(false);
    const [loadingTemplates, setLoadingTemplates] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [needsAuth, setNeedsAuth] = useState(false);
    
    // Create template dialog state
    const [createDialogOpen, setCreateDialogOpen] = useState(false);
    const [creating, setCreating] = useState(false);
    const [createError, setCreateError] = useState<string | null>(null);
    const [createSuccess, setCreateSuccess] = useState(false);
    const [formData, setFormData] = useState({
        name: '',
        language: 'es_ES',
        category: 'UTILITY' as 'MARKETING' | 'UTILITY' | 'AUTHENTICATION',
        bodyText: '',
        exampleBodyText: ''
    });

    const applyMeetingPreset = (presetId: MeetingAutomationTemplatePresetId) => {
        const preset = MEETING_AUTOMATION_TEMPLATE_PRESETS[presetId];
        setFormData((current) => ({
            ...current,
            name: current.name.trim() ? current.name : preset.suggestedName,
            language: 'es_ES',
            category: 'UTILITY',
            bodyText: preset.bodyText,
            exampleBodyText: preset.exampleValuesLine,
        }));
    };

    useEffect(() => {
        if (!hasAuthToken) {
            return;
        }

        if (loadingWhatsAppStatus) {
            return;
        }

        if (whatsappStatus?.connected === false) {
            setNeedsAuth(true);
            setError(null);
            setWabas([]);
            setSelectedWaba('');
            setTemplates([]);
            return;
        }

        if (whatsappStatusError) {
            setNeedsAuth(false);
            setError(whatsappStatusError instanceof Error ? whatsappStatusError.message : t('error.generic'));
            return;
        }

        if (!whatsappStatus?.connected) {
            return;
        }

        let isActive = true;

        const fetchWabas = async () => {
            setLoadingWabas(true);
            setError(null);
            setNeedsAuth(false);
            try {
                if (!token || !token.trim()) {
                    console.error('No token available for fetching WABAs');
                    setError('Authentication token not available');
                    return;
                }
                
                const allWabas = await whatsappService.getWabas();
                if (!isActive) {
                    return;
                }
                setWabas(allWabas);

                const preferredWabaId = resolvePreferredWhatsAppWabaId(
                    allWabas,
                    whatsappStatus?.account_id,
                );
                if (preferredWabaId) {
                    setSelectedWaba(preferredWabaId);
                }
            } catch (err) {
                const errorMessage = err instanceof Error ? err.message.toLowerCase() : '';
                if (
                    errorMessage.includes('token') ||
                    errorMessage.includes('connect') ||
                    errorMessage.includes('facebook account') ||
                    errorMessage.includes('authentication required')
                ) {
                    setNeedsAuth(true);
                    setError(null);
                    return;
                }
                if (!needsAuth) {
                    setError(err instanceof Error ? err.message : t('error.generic'));
                }
            } finally {
                if (isActive) {
                    setLoadingWabas(false);
                }
            }
        };

        if (token && token.trim()) {
            fetchWabas();
        }

        return () => {
            isActive = false;
        };
    }, [hasAuthToken, loadingWhatsAppStatus, t, token, whatsappStatus?.connected, whatsappStatusError]);

    useEffect(() => {
        const fetchTemplates = async () => {
            if (!selectedWaba) return;

            setLoadingTemplates(true);
            try {
                if (!token || !token.trim()) {
                    console.error('No token available for fetching templates');
                    return;
                }

                const data = await whatsappService.getTemplates(selectedWaba);
                setTemplates(data);
            } catch (err) {
                console.error(err);
            } finally {
                setLoadingTemplates(false);
            }
        };

        if (token && token.trim()) {
            fetchTemplates();
        }
    }, [selectedWaba, token]);

    const handleCreateTemplate = async () => {
        if (!selectedWaba) {
            setCreateError('Please select a WABA first');
            return;
        }

        // Validation
        if (!formData.name.trim()) {
            setCreateError('Template name is required');
            return;
        }
        if (!formData.bodyText.trim()) {
            setCreateError('Body text is required');
            return;
        }

        setCreating(true);
        setCreateError(null);
        setCreateSuccess(false);

        try {
            // Parse example body text if provided
            let exampleBodyText: string[][] | undefined;
            if (formData.exampleBodyText.trim()) {
                // Simple parsing: split by newlines, then by commas or spaces
                const lines = formData.exampleBodyText.trim().split('\n').filter(l => l.trim());
                exampleBodyText = lines.map(line => {
                    // Try splitting by comma first, then by space
                    const values = line.includes(',') 
                        ? line.split(',').map(v => v.trim()).filter(v => v)
                        : line.split(/\s+/).filter(v => v);
                    return values;
                });
            }

            await whatsappService.createTemplate(
                selectedWaba,
                formData.name.trim(),
                formData.language,
                formData.category,
                formData.bodyText.trim(),
                exampleBodyText
            );

            setCreateSuccess(true);
            
            // Reset form
            setFormData({
                name: '',
                language: 'es_ES',
                category: 'UTILITY',
                bodyText: '',
                exampleBodyText: ''
            });

            // Refresh templates list
            const updatedTemplates = await whatsappService.getTemplates(selectedWaba);
            setTemplates(updatedTemplates);

            // Close dialog after short delay
            setTimeout(() => {
                setCreateDialogOpen(false);
                setCreateSuccess(false);
            }, 1500);
        } catch (err) {
            console.error('Error creating template:', err);
            setCreateError(err instanceof Error ? err.message : 'Failed to create template');
        } finally {
            setCreating(false);
        }
    };

    return (
        <Card>
            <CardHeader>
                <CardTitle className="flex justify-between items-center">
                    WhatsApp Templates
                    {(loadingWabas || loadingWhatsAppStatus) && <Loader2 className="h-4 w-4 animate-spin" />}
                </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
                {needsAuth && lang ? (
                    <AutomationSettingsLinkAlert
                        lang={lang}
                        message={AUTOMATION_TEMPLATE_UI.whatsappRequiredMessage}
                    />
                ) : null}
                {error && !needsAuth && (
                    <Alert variant="destructive">
                        <AlertCircle className="h-4 w-4" />
                        <AlertDescription>{error}</AlertDescription>
                    </Alert>
                )}

                <div className="flex items-center space-x-2">
                    <span className="text-sm font-medium">WABA:</span>
                    <Select value={selectedWaba} onValueChange={setSelectedWaba} disabled={loadingWabas || loadingWhatsAppStatus || needsAuth}>
                        <SelectTrigger className="w-[280px]">
                            <SelectValue placeholder="Select WABA" />
                        </SelectTrigger>
                        <SelectContent>
                            {wabas.map((waba) => (
                                <SelectItem key={waba.id} value={waba.id}>
                                    {waba.name} ({waba.id})
                                </SelectItem>
                            ))}
                        </SelectContent>
                    </Select>
                </div>

                <div className="rounded-md border">
                    <Table>
                        <TableHeader>
                            <TableRow>
                                <TableHead>Name</TableHead>
                                <TableHead>Status</TableHead>
                                <TableHead>Language</TableHead>
                                <TableHead>Category</TableHead>
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            {loadingTemplates ? (
                                <TableRow>
                                    <TableCell colSpan={4} className="text-center py-4">
                                        <Loader2 className="h-6 w-6 animate-spin mx-auto" />
                                    </TableCell>
                                </TableRow>
                            ) : templates.length === 0 ? (
                                <TableRow>
                                    <TableCell colSpan={4} className="text-center py-4 text-muted-foreground">
                                        No templates found.
                                    </TableCell>
                                </TableRow>
                            ) : (
                                templates.map((template) => (
                                    <TableRow key={template.name + template.language}>
                                        <TableCell className="font-medium">{template.name}</TableCell>
                                        <TableCell>
                                            <Badge variant={template.status === 'APPROVED' ? 'default' : 'secondary'}>
                                                {template.status}
                                            </Badge>
                                        </TableCell>
                                        <TableCell>{template.language}</TableCell>
                                        <TableCell>{template.category}</TableCell>
                                    </TableRow>
                                ))
                            )}
                        </TableBody>
                    </Table>
                </div>

                {/* Create Template Dialog */}
                <Dialog open={createDialogOpen} onOpenChange={setCreateDialogOpen}>
                    <DialogContent className="sm:max-w-[600px]">
                        <DialogHeader>
                            <DialogTitle>
                                {t('whatsapp_templates.create_template') || 'Create WhatsApp Template'}
                            </DialogTitle>
                            <DialogDescription>
                                {t('whatsapp_templates.create_template_description') || 
                                'Create a new WhatsApp message template.'}
                            </DialogDescription>
                        </DialogHeader>

                        {createSuccess ? (
                            <div className="flex flex-col items-center py-6 text-success">
                                <CheckCircle className="h-12 w-12 mb-2" />
                                <p className="font-medium">
                                    {t('whatsapp_templates.create_success') || 'Template created successfully!'}
                                </p>
                                <p className="text-sm text-muted-foreground mt-2">
                                    {t('whatsapp_templates.create_success_note') || 
                                    'The template is pending approval from Meta.'}
                                </p>
                            </div>
                        ) : (
                            <div className="space-y-4 py-4">
                                <div className="space-y-2">
                                    <Label htmlFor="template-name">
                                        {t('whatsapp_templates.template_name') || 'Template Name'} *
                                    </Label>
                                    <Input
                                        id="template-name"
                                        placeholder="welcome_message"
                                        value={formData.name}
                                        onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                                        disabled={creating}
                                    />
                                    <p className="text-xs text-muted-foreground">
                                        {t('whatsapp_templates.template_name_hint') || 
                                        'Only letters, numbers, underscores, and hyphens'}
                                    </p>
                                </div>

                                <div className="grid grid-cols-2 gap-4">
                                    <div className="space-y-2">
                                        <Label htmlFor="template-language">
                                            {t('whatsapp_templates.language') || 'Language'} *
                                        </Label>
                                        <Select
                                            value={formData.language}
                                            onValueChange={(value) => setFormData({ ...formData, language: value })}
                                            disabled={creating}
                                        >
                                            <SelectTrigger>
                                                <SelectValue />
                                            </SelectTrigger>
                                            <SelectContent>
                                                <SelectItem value="es_ES">Spanish (es_ES)</SelectItem>
                                                <SelectItem value="en_US">English (en_US)</SelectItem>
                                                <SelectItem value="fr_FR">French (fr_FR)</SelectItem>
                                                <SelectItem value="de_DE">German (de_DE)</SelectItem>
                                                <SelectItem value="it_IT">Italian (it_IT)</SelectItem>
                                                <SelectItem value="pt_PT">Portuguese (pt_PT)</SelectItem>
                                            </SelectContent>
                                        </Select>
                                    </div>

                                    <div className="space-y-2">
                                        <Label htmlFor="template-category">
                                            {t('whatsapp_templates.category') || 'Category'} *
                                        </Label>
                                        <Select
                                            value={formData.category}
                                            onValueChange={(value: 'MARKETING' | 'UTILITY' | 'AUTHENTICATION') => 
                                                setFormData({ ...formData, category: value })}
                                            disabled={creating}
                                        >
                                            <SelectTrigger>
                                                <SelectValue />
                                            </SelectTrigger>
                                            <SelectContent>
                                                <SelectItem value="UTILITY">
                                                    {t('whatsapp_templates.category_utility') || 'Utility'}
                                                </SelectItem>
                                                <SelectItem value="MARKETING">
                                                    {t('whatsapp_templates.category_marketing') || 'Marketing'}
                                                </SelectItem>
                                                <SelectItem value="AUTHENTICATION">
                                                    {t('whatsapp_templates.category_authentication') || 'Authentication'}
                                                </SelectItem>
                                            </SelectContent>
                                        </Select>
                                    </div>
                                </div>

                                <div className="space-y-2">
                                    <Label htmlFor="template-body">
                                        {t('whatsapp_templates.body_text') || 'Body Text'} *
                                    </Label>
                                    <Textarea
                                        id="template-body"
                                        placeholder="Hello, welcome to our service!"
                                        value={formData.bodyText}
                                        onChange={(e) => setFormData({ ...formData, bodyText: e.target.value })}
                                        disabled={creating}
                                        rows={4}
                                    />
                                    <p className="text-xs text-muted-foreground">
                                        {t('whatsapp_templates.body_text_hint', {
                                            defaultValue: 'Use {{1}}, {{2}}, etc. for variables. Max 1024 characters.',
                                        })}
                                    </p>
                                </div>

                                <div className="rounded-lg border bg-muted/40 p-4 space-y-2">
                                    <div className="flex flex-wrap items-center justify-between gap-2">
                                        <div>
                                            <p className="text-sm font-medium">
                                                {t('whatsapp_templates.meeting_automation_contract_title', {
                                                    defaultValue: 'Visit automation variable contract',
                                                })}
                                            </p>
                                            <p className="text-xs text-muted-foreground mt-1">
                                                {t('whatsapp_templates.meeting_automation_contract_description', {
                                                    defaultValue:
                                                        'If this template will be used for pre/post visit automation, placeholders must follow this exact semantic order.',
                                                })}
                                            </p>
                                        </div>
                                        <div className="flex flex-wrap gap-2">
                                            <Button
                                                type="button"
                                                variant="outline"
                                                size="sm"
                                                onClick={() => applyMeetingPreset('pre_visit')}
                                                disabled={creating}
                                            >
                                                {t('whatsapp_templates.apply_pre_visit_preset', {
                                                    defaultValue: 'Use pre-visit preset',
                                                })}
                                            </Button>
                                            <Button
                                                type="button"
                                                variant="outline"
                                                size="sm"
                                                onClick={() => applyMeetingPreset('post_visit')}
                                                disabled={creating}
                                            >
                                                {t('whatsapp_templates.apply_post_visit_preset', {
                                                    defaultValue: 'Use post-visit preset',
                                                })}
                                            </Button>
                                        </div>
                                    </div>
                                    <div className="grid gap-2 sm:grid-cols-2">
                                        {CANONICAL_MEETING_VARIABLE_SPECS.map((spec) => (
                                            <div key={spec.slot} className="rounded-md border bg-background px-3 py-2 text-xs">
                                                <div className="font-medium">{spec.slot} {spec.key}</div>
                                                <div className="text-muted-foreground">{spec.label}</div>
                                            </div>
                                        ))}
                                    </div>
                                </div>

                                <div className="space-y-2">
                                    <Label htmlFor="template-example-values">
                                        {t('whatsapp_templates.example_values', {
                                            defaultValue: 'Example Values (Optional)',
                                        })}
                                    </Label>
                                    <Textarea
                                        id="template-example-values"
                                        placeholder={CANONICAL_MEETING_VARIABLE_SPECS.map((spec) => spec.example).join(', ')}
                                        value={formData.exampleBodyText}
                                        onChange={(e) => setFormData({ ...formData, exampleBodyText: e.target.value })}
                                        disabled={creating}
                                        rows={3}
                                    />
                                    <p className="text-xs text-muted-foreground">
                                        {t('whatsapp_templates.example_hint', {
                                            defaultValue:
                                                'One example row per line. Values must follow the same order as {{1}}, {{2}}, etc.',
                                        })}
                                    </p>
                                </div>

                                {createError && (
                                    <Alert variant="destructive">
                                        <AlertCircle className="h-4 w-4" />
                                        <AlertDescription>{createError}</AlertDescription>
                                    </Alert>
                                )}
                            </div>
                        )}

                        <DialogFooter>
                            <Button
                                variant="outline"
                                onClick={() => setCreateDialogOpen(false)}
                                disabled={creating || createSuccess}
                            >
                                {createSuccess 
                                    ? (t('common.close') || 'Close')
                                    : (t('common.cancel') || 'Cancel')
                                }
                            </Button>
                            {!createSuccess && (
                                <Button onClick={handleCreateTemplate} disabled={creating}>
                                    {creating ? (
                                        <>
                                            <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                                            {t('whatsapp_templates.creating') || 'Creating...'}
                                        </>
                                    ) : (
                                        <>
                                            <Plus className="h-4 w-4 mr-2" />
                                            {t('whatsapp_templates.create') || 'Create'}
                                        </>
                                    )}
                                </Button>
                            )}
                        </DialogFooter>
                    </DialogContent>
                </Dialog>
            </CardContent>
        </Card>
    );
}
